import { HttpEvent, HttpEventType } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import {
  catchError,
  EMPTY,
  exhaustMap,
  map,
  of,
  switchMap,
  takeUntil,
  tap,
  timer,
  withLatestFrom,
} from "rxjs";
import { filter } from "rxjs/operators";

import { NotificationService } from "../../_services/notification.service";
import { isFeatureEnabledSelector } from "../feature/feature.selectors";
import { AppState } from "../store";
import * as DocumentActions from "./document.actions";
import { UPLOAD_WINDOW_EXPIRED_MESSAGE } from "./document.model";
import {
  currentDocumentSelector,
  modelTypeSelector,
  resolvedOptionsSelector,
  uploadSessionSelector,
} from "./document.selectors";
import { DocumentService } from "./document.service";

const ANALYZE_V2_FLAG = "EnableAnalyzeV2Experiment";
// Pandoc conversion has no v2 endpoint, so it always uses the v1 flow.
const PANDOC_MODEL = "Pandoc";
// Shown when an authorized call comes back 401 — the session/token expired and
// the global interceptor's silent refresh could not recover it.
const SESSION_EXPIRED_MESSAGE =
  "Your session has expired. Please refresh the page to sign in again, then retry.";

// Shape of the error objects thrown by HttpClient / the document service.
// `error` is the response body — a string for portal JSON errors, but raw
// Azure XML or a ProgressEvent on SAS failures (see toBlobErrorMessage) — so
// it's typed `unknown` rather than `string` to force callers to check before
// display. `message` is HttpClient's human string.
type ApiErrorLike = { status?: number; error?: unknown; message?: string };

@Injectable()
export class DocumentEffects {
  private actions$ = inject(Actions);
  private documentService = inject(DocumentService);
  private notificationService = inject(NotificationService);
  private store = inject<Store<AppState>>(Store);

  // v1 / Pandoc: single multipart upload. The v2 path is the stepped
  // initialize → upload → process flow below.
  // exhaustMap: while a POST is in-flight, duplicate dispatches (e.g. rapid
  // retry clicks) are dropped so a single request is always in progress.
  // takeUntil: cancelling while the POST is in flight (resetState) aborts
  // the request so a late success cannot repopulate state after the user
  // has already moved on (matching initializeUpload$ and uploadToBlob$).
  analyzeDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.analyzeDocument),
      exhaustMap(action =>
        this.documentService.analyzeDocument(action.document.file).pipe(
          map(result => DocumentActions.analyzeDocumentSuccess({ result })),
          catchError(error =>
            of(
              DocumentActions.analyzeDocumentFailure({
                error: error?.error || error.message,
              })
            )
          ),
          takeUntil(this.actions$.pipe(ofType(DocumentActions.resetState)))
        )
      )
    )
  );

  // v2 step 1: reserve the request and obtain the SAS URL to show the user.
  initializeUpload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.initializeUpload),
      // exhaustMap (not mergeMap): while an initialize is in flight, a second
      // dispatch must be ignored — racing inits would overwrite the session
      // and desync the SAS URL from the request the API actually reserved.
      exhaustMap(action =>
        this.documentService.initializeUpload(action.document.file).pipe(
          map(response =>
            DocumentActions.initializeUploadSuccess({
              session: {
                requestId: response.requestId,
                uploadUrl: response.uploadUrl,
                expiresAt: response.expiresAt,
              },
            })
          ),
          catchError(error =>
            of(
              DocumentActions.initializeUploadFailure({
                error: this.toInitErrorMessage(error),
              })
            )
          ),
          // Cancelling while initialize is in flight (resetState) aborts the
          // request and prevents a late success from re-opening the panel.
          takeUntil(this.actions$.pipe(ofType(DocumentActions.resetState)))
        )
      )
    )
  );

  // After init succeeds, automatically trigger the portal upload so no further
  // user action is required between step 1 and receiving results.
  // timer(0) defers the dispatch by one event-loop turn so Angular can paint
  // the upload-session panel at 0% before the XHR starts. Without the delay,
  // fast uploads complete before the first animation frame and the progress bar
  // is never rendered. takeUntil cancels the deferred dispatch if the user
  // clicks Cancel during the brief window between init and upload start.
  autoUploadAfterInit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.initializeUploadSuccess),
      switchMap(() =>
        timer(0).pipe(
          map(() => DocumentActions.uploadToBlob()),
          takeUntil(this.actions$.pipe(ofType(DocumentActions.resetState)))
        )
      )
    )
  );

  // After the upload succeeds, automatically enqueue processing using the
  // resolved options captured at step 1 (stored in state by initializeUpload).
  // If options are null the user cancelled (resetState cleared them) between
  // upload completion and this dispatch — return EMPTY to avoid reopening a
  // blank progress panel via processUploadFailure.
  autoProcessAfterUpload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.uploadToBlobSuccess),
      withLatestFrom(this.store.select(resolvedOptionsSelector)),
      switchMap(([, resolvedOptions]) => {
        if (!resolvedOptions) return EMPTY;
        return of(DocumentActions.processUpload({ options: resolvedOptions }));
      })
    )
  );

  // v2 step 2 (optional): the portal PUTs the file to the SAS URL on the user's
  // behalf. Users may instead upload directly to Azure and skip this.
  uploadToBlob$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.uploadToBlob),
      withLatestFrom(
        this.store.select(uploadSessionSelector),
        this.store.select(currentDocumentSelector)
      ),
      switchMap(([, session, document]) => {
        if (!session || !document) {
          return of(
            DocumentActions.uploadToBlobFailure({
              error: "No active upload session.",
            })
          );
        }
        const sourceUrl = document.file.sourceUrl;
        const upload$ = sourceUrl
          ? this.documentService.copyFromUrl(
              session.uploadUrl,
              sourceUrl,
              document.file.type || undefined
            )
          : this.documentService.uploadToSas(session.uploadUrl, document.file);
        return upload$.pipe(
          map(event => this.mapUploadEvent(event, !!sourceUrl)),
          filter((action): action is Action => action !== null),
          catchError(error =>
            of(
              DocumentActions.uploadToBlobFailure({
                error: this.toBlobErrorMessage(error, !!sourceUrl),
              })
            )
          ),
          // Unsubscribe the in-flight PUT when the user cancels. Without this,
          // a late success would trigger autoProcessAfterUpload$ and dispatch
          // processUpload against a cleared session, failing and reopening a
          // blank progress panel.
          takeUntil(this.actions$.pipe(ofType(DocumentActions.resetState)))
        );
      })
    )
  );

  // v2 step 3: validate the uploaded blob and enqueue processing, then hand off
  // to the existing results-polling flow.
  processUpload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.processUpload),
      withLatestFrom(this.store.select(uploadSessionSelector)),
      switchMap(([action, session]) => {
        if (!session) {
          return of(
            DocumentActions.processUploadFailure({
              error: "No active upload session.",
            })
          );
        }
        return this.documentService
          .processUpload(session.requestId, action.options)
          .pipe(
            map(() =>
              DocumentActions.documentResult({ id: session.requestId })
            ),
            catchError(error =>
              of(
                DocumentActions.processUploadFailure({
                  error: this.toProcessErrorMessage(error),
                  expired: error?.status === 410,
                })
              )
            )
          );
      })
    )
  );

  uploadToBlobSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentActions.uploadToBlobSuccess),
        tap(() =>
          this.notificationService.showSuccess("File uploaded to storage.")
        )
      ),
    { dispatch: false }
  );

  // Translate the PUT's HttpEvents into progress/success actions; other event
  // types (Sent, ResponseHeader, …) are ignored.
  private mapUploadEvent(
    event: HttpEvent<unknown>,
    isUrlCopy = false
  ): Action | null {
    if (event.type === HttpEventType.UploadProgress) {
      const percent = event.total
        ? Math.round((100 * event.loaded) / event.total)
        : 0;
      return DocumentActions.uploadToBlobProgress({ percent });
    }
    if (event.type === HttpEventType.Response) {
      const status = event.status;
      if (isUrlCopy && status !== 201) {
        return DocumentActions.uploadToBlobFailure({
          error:
            "The source file could not be copied synchronously (it may be " +
            "too large for this path). Please try again or upload the file directly.",
        });
      }
      return DocumentActions.uploadToBlobSuccess();
    }
    return null;
  }

  // Translate a thrown API error into a user-facing message. statusMessages
  // maps an HTTP status to either a fixed string or a function (when the
  // message depends on the body); fallback handles every other status. Each
  // caller owns its fallback so the lossy choices stay explicit — notably blob
  // errors must never echo error.error (raw Azure XML / a ProgressEvent).
  private translateApiError(
    error: ApiErrorLike,
    statusMessages: Record<number, string | ((e: ApiErrorLike) => string)>,
    fallback: (e: ApiErrorLike) => string
  ): string {
    const handler = statusMessages[error?.status ?? -1];
    if (handler !== undefined) {
      return typeof handler === "function" ? handler(error) : handler;
    }
    return fallback(error);
  }

  // error.error is untyped at the HttpClient boundary; only surface it when
  // it's actually a display-safe string, otherwise callers would render
  // "[object Object]"/"[object ProgressEvent]" for a non-string body.
  private stringErrorBody(error: ApiErrorLike): string | undefined {
    return typeof error?.error === "string" ? error.error : undefined;
  }

  private toInitErrorMessage(error: ApiErrorLike): string {
    return this.translateApiError(
      error,
      { 401: SESSION_EXPIRED_MESSAGE },
      e =>
        this.stringErrorBody(e) || e?.message || "Could not start the upload."
    );
  }

  // Azure returns the same "AuthenticationFailed" reason for both an expired
  // SAS and a malformed signature, so blob 401/403 can't be distinguished from
  // one another — both fall through to the generic fallback below. For the
  // "Use a file URL" copy path, Azure Blob's XML body may also echo the
  // source URL's query string, so isUrlCopy branches to guidance that never
  // surfaces error.error at all.
  private toBlobErrorMessage(error: ApiErrorLike, isUrlCopy = false): string {
    return this.translateApiError(
      error,
      {
        // status 0 means the browser never received an HTTP response: a CORS,
        // network, or proxy block (e.g. Zscaler killing direct Azure storage
        // traffic on a managed device).
        0: "Couldn't reach file storage. Check your connection — on a TR device, make sure Zscaler/VPN is on — then try again.",
        400: () =>
          isUrlCopy
            ? "Could not reach the source URL. Check that it's publicly accessible " +
              "and any signed URL hasn't expired, then try again."
            : this.genericBlobFailureMessage(),
      },
      () =>
        isUrlCopy
          ? "The file could not be copied from the source URL. Please try again."
          : this.genericBlobFailureMessage()
    );
  }

  private genericBlobFailureMessage(): string {
    return "Upload failed. Retry, or upload directly to Azure with the URL above.";
  }

  private toProcessErrorMessage(error: ApiErrorLike): string {
    return this.translateApiError(
      error,
      {
        401: SESSION_EXPIRED_MESSAGE,
        410: UPLOAD_WINDOW_EXPIRED_MESSAGE,
        400: e =>
          this.stringErrorBody(e) ||
          "The file could not be processed. Make sure it finished uploading.",
      },
      e =>
        this.stringErrorBody(e) ||
        e?.message ||
        "Processing could not be started."
    );
  }

  // No failure toast for the analyze flow. Every failure action already sets
  // documentError, which the upload view shows via <app-error>. Toasting here
  // would double-report the same error. This keeps v1/Pandoc/file-validation
  // failures consistent with the v2 initialize/upload/process paths, which are
  // surfaced through <app-error> only.

  analyzeDocumentSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.analyzeDocumentSuccess),
      switchMap(action => {
        this.notificationService.showSuccess("Document uploaded successfully!");
        return of(DocumentActions.documentResult({ id: action.result.id }));
      })
    )
  );

  getDocumentResult$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.documentResult),
      withLatestFrom(
        this.store.select(modelTypeSelector),
        this.store.select(isFeatureEnabledSelector(ANALYZE_V2_FLAG))
      ),
      switchMap(([action, modelType, v2Enabled]) => {
        const useV2 = v2Enabled && modelType !== PANDOC_MODEL;
        const result$ = useV2
          ? this.documentService.getDocumentResultV2(action.id)
          : this.documentService.getDocumentResult(
              action.id,
              modelType ?? undefined
            );
        return result$.pipe(
          map(result => DocumentActions.documentResultSuccess({ result })),
          catchError(error =>
            of(
              DocumentActions.documentResultFailure({
                error: this.translateApiError(
                  error,
                  { 401: SESSION_EXPIRED_MESSAGE },
                  e =>
                    e?.message ||
                    "Could not retrieve results. Please try again."
                ),
              })
            )
          ),
          // Cancel an in-flight GET if the user resets state so a late
          // success cannot repopulate documentResult after the panel is gone.
          takeUntil(this.actions$.pipe(ofType(DocumentActions.resetState)))
        );
      })
    )
  );

  // Re-polls the result endpoint while the job is still in progress.
  // Previously this lived in doc-result.component; moved here so polling fires
  // while the upload-session panel is shown (results screen only renders on
  // Completed). Uses exponential backoff (2.5 s → 5 s → 10 s → 20 s → 40 s
  // cap) to bound request volume for long-running jobs, matching the replaced
  // poller. pollCount resets when a new job id is seen. Cancelled immediately
  // if the user resets state (e.g. clicks Cancel).
  pollForResult$ = createEffect(() => {
    let pollCount = 0;
    let trackedId = "";
    return this.actions$.pipe(
      ofType(DocumentActions.documentResultSuccess),
      filter(
        action =>
          action.result.status === "Pending" ||
          action.result.status === "AwaitingUpload"
      ),
      switchMap(action => {
        if (action.result.id !== trackedId) {
          pollCount = 0;
          trackedId = action.result.id;
        }
        const delay = Math.min(2500 * Math.pow(2, pollCount), 40_000);
        pollCount++;
        return timer(delay).pipe(
          map(() => DocumentActions.documentResult({ id: action.result.id })),
          takeUntil(this.actions$.pipe(ofType(DocumentActions.resetState)))
        );
      })
    );
  });

  // v2 returns results === null once the inline payload exceeds the API cap;
  // fetch the full results JSON from the SAS URI and merge it into state.
  getResultsFromBlob$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.documentResultSuccess),
      filter(
        action =>
          action.result?.status === "Completed" &&
          !action.result?.results &&
          !!action.result?.resultsBlobUri
      ),
      switchMap(action =>
        this.documentService
          .getDocumentResultsFromBlob(action.result.resultsBlobUri as string)
          .pipe(
            map(results =>
              DocumentActions.documentResultsBlobSuccess({ results })
            ),
            catchError(error =>
              of(
                DocumentActions.documentResultsBlobFailure({
                  error:
                    error?.message ||
                    "Could not download the full results. Please try again.",
                })
              )
            )
          )
      )
    )
  );

  getSupportedFileTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.getSupportedFileTypes),
      switchMap(() =>
        this.documentService.getSupportedFileTypes().pipe(
          map(fileTypes =>
            DocumentActions.setSupportedFileTypes({
              supportedFileTypes: fileTypes.supportedFileTypes,
            })
          )
        )
      )
    )
  );

  getFileSizeLimit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.getFileSizeLimit),
      switchMap(() =>
        this.documentService.getMaxFileSizeBytes().pipe(
          map(fileSize =>
            DocumentActions.setFileSizeLimit({
              maxFileSizeBytes: fileSize.maxFileSizeBytes,
            })
          )
        )
      )
    )
  );

  getPdfConverted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.documentResultSuccess),
      filter(action => !!action.result?.additionalContent?.pdfBlobUri),
      switchMap(action =>
        this.documentService
          .getDocumentConvertedPdf(action.result.additionalContent.pdfBlobUri)
          .pipe(
            map(pdfFile =>
              DocumentActions.documentConvertedPdfSuccess({ pdfFile })
            ),
            catchError(error =>
              of(
                DocumentActions.documentResultFailure({ error: error.message })
              )
            )
          )
      )
    )
  );

  getTablesData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.documentResultSuccess),
      filter(action => !!action.result?.additionalContent?.tablesBlobUri),
      switchMap(action =>
        this.documentService
          .getDocumentTablesResult(
            action.result.additionalContent.tablesBlobUri
          )
          .pipe(
            map(tablesData =>
              DocumentActions.documentTablesDataSuccess({ tablesData })
            ),
            catchError(error =>
              of(
                DocumentActions.documentTablesDataFailure({
                  error: error.message,
                })
              )
            )
          )
      )
    )
  );

  getTranscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.documentResultSuccess),
      filter(action => !!action.result?.audioContent?.transcriptionBlobUri),
      switchMap(action =>
        this.documentService
          .getTranscriptionResult(
            action.result.audioContent!.transcriptionBlobUri
          )
          .pipe(
            map(transcription =>
              DocumentActions.documentTranscriptionSuccess({ transcription })
            ),
            catchError(error =>
              of(
                DocumentActions.documentTranscriptionFailure({
                  error: error.message,
                })
              )
            ),
            // Cancel while in flight on reset (new upload or file removal) so
            // a late response can't repopulate the reset/new document with
            // the previous document's transcript.
            takeUntil(this.actions$.pipe(ofType(DocumentActions.resetState)))
          )
      )
    )
  );

  getAudio$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.documentResultSuccess),
      filter(action => !!action.result?.audioContent?.audioBlobUri),
      switchMap(action =>
        this.documentService
          .getDocumentAudio(action.result.audioContent!.audioBlobUri)
          .pipe(
            map(audioBlob =>
              DocumentActions.documentAudioSuccess({ audioBlob })
            ),
            catchError(error =>
              of(
                DocumentActions.documentAudioFailure({
                  error: error.message,
                })
              )
            ),
            // Cancel while in flight on reset (new upload or file removal) so
            // a late response can't repopulate the reset/new document with
            // the previous document's audio.
            takeUntil(this.actions$.pipe(ofType(DocumentActions.resetState)))
          )
      )
    )
  );
}
